import React from "react";
import Card from "components/card";
import SearchIcon from "components/icons/SearchIcon";
import { MdChevronRight, MdChevronLeft, MdPerson, MdTrendingUp, MdTrendingDown, MdTune } from "react-icons/md";
import UserProfileModal from "components/modal/UserProfileModal";
import UserPreferencesModal from "components/modal/UserPreferencesModal";
import { EnhancedUser } from "types/profile.types";
import notificationService from "@services/notificationService";
import Avatar from "components/avatar/Avatar";
import { promoteUserToMentor, demoteUserFromMentor } from "@services/cloudFunctions";
import ConfirmPopup from "components/popup/ConfirmPopup";
import userDataService from "@services/userDataService";

import {
  PaginationState,
  createColumnHelper,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

type RowObj = {
  name: string[];
  email: string;
  username: string;
  date: string;
  type: string;
  actions: null;
  uid: string;
  status: 'active' | 'inactive' | 'disabled';
};

function CheckTable(props: {
  tableData: any;
  tenantId?: string | null;
  tenantName?: string;
  onRefresh?: () => void;
  onUpdateSingleUser?: (uid: string) => Promise<void>;
  users?: EnhancedUser[];
}) {
  const { tableData, users, onUpdateSingleUser, tenantId, tenantName } = props;
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  let defaultData = tableData;
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<EnhancedUser | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [preferencesUser, setPreferencesUser] = React.useState<EnhancedUser | null>(null);
  const [isPreferencesOpen, setIsPreferencesOpen] = React.useState(false);
  const [loadingUserId, setLoadingUserId] = React.useState<string | null>(null);
  const [confirmConfig, setConfirmConfig] = React.useState({
    isOpen: false,
    message: '',
    title: 'Confirm',
    onConfirm: () => {},
  });

  // Execute the role change (internal function)
  const executeRoleChange = async (
    userId: string,
    userName: string,
    action: 'promote' | 'demote'
  ) => {
    const actionText = action === 'promote' ? 'promote' : 'demote';
    setLoadingUserId(userId);
    try {
      const result = action === 'promote'
        ? await promoteUserToMentor(userId)
        : await demoteUserFromMentor(userId);

      if (result.success) {
        notificationService.success(
          `${userName} has been ${action === 'promote' ? 'promoted to' : 'demoted from'} Mentor!`
        );

        // Invalidate cache for the updated user
        userDataService.invalidateUserCache(userId);

        // Invalidate tenant users cache to ensure list is refreshed
        if (tenantId) {
          userDataService.invalidateTenantUsersCache(tenantId);
        }

        // Only fetch the single updated user instead of all users
        if (onUpdateSingleUser) {
          await onUpdateSingleUser(userId);
        }
      } else {
        notificationService.error(result.error || `Failed to ${actionText} user`);
      }
    } catch (error: any) {
      console.error(`${actionText} failed:`, error);
      notificationService.error(error.message || `Failed to ${actionText} user`);
    } finally {
      setLoadingUserId(null);
    }
  };

  // Handle user role change (promote/demote) - shows confirmation popup
  const handleRoleChange = (
    userId: string,
    userName: string,
    action: 'promote' | 'demote'
  ) => {
    const confirmMessage = action === 'promote'
      ? `Promote ${userName} to Mentor?\n\nThis will allow them to be matched as a mentor in future matches. Existing introductions and scheduled matches will not be affected.`
      : `Demote ${userName} from Mentor role?\n\nThey will no longer be matched as a mentor in future matches. Existing introductions and scheduled matches will not be affected.`;

    setConfirmConfig({
      isOpen: true,
      message: confirmMessage,
      title: action === 'promote' ? 'Promote to Mentor' : 'Demote from Mentor',
      onConfirm: () => executeRoleChange(userId, userName, action),
    });
  };

  const createPages = (count: number) => {
    let arrPageCount = [];

    for (let i = 1; i <= count; i++) {
      arrPageCount.push(i);
    }

    return arrPageCount;
  };
  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          USER NAME
        </p>
      ),
      cell: (info: any) => {
        const [name, uid] = info.getValue();

        return (
          <div className="flex items-center gap-3">
            <Avatar
              uid={uid}
              displayName={name}
              size="small"
            />
            <div>
              <p className="font-medium text-navy-700 dark:text-white">
                {name}
              </p>
            </div>
          </div>
        );
      },
      filterFn: (row, columnId, filterValue) => {
        const [name] = row.getValue(columnId) as string[];
        return name.toLowerCase().includes(filterValue.toLowerCase());
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">EMAIL</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("date", {
      id: "date",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          JOIN DATE
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          USER TYPE
        </p>
      ),
      cell: (info) => {
        const type = info.getValue();
        return (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
              type === "Mentor"
                ? "bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-300"
                : type === "Professional"
                ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300"
                : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
            }`}
          >
            {type}
          </span>
        );
      },
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          ACTIONS
        </p>
      ),
      cell: (info) => {
        const rowData = info.row.original;
        const [userName] = rowData.name;
        const isLoading = loadingUserId === rowData.uid;

        return (
          <div className="flex gap-2">
            <button
              onClick={() => {
                const user = users?.find(u => u.uid === rowData.uid);
                if (user) {
                  setSelectedUser(user);
                  setIsModalOpen(true);
                }
              }}
              className="flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 transition duration-200 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/30"
            >
              <MdPerson className="h-3 w-3" />
              Profile
            </button>
            <button
              onClick={() => {
                const user = users?.find(u => u.uid === rowData.uid);
                if (user) {
                  setPreferencesUser(user);
                  setIsPreferencesOpen(true);
                }
              }}
              className="flex items-center gap-1 rounded-md bg-purple-50 border border-purple-200 px-2 py-1 text-xs font-medium text-purple-700 transition duration-200 hover:bg-purple-100 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-900/30"
            >
              <MdTune className="h-3 w-3" />
              Preferences
            </button>
            {rowData.status === "disabled" ? (
              <button
                onClick={() => {
                  notificationService.info("This functionality will come soon!");
                }}
                className="flex items-center gap-1 rounded-md bg-green-50 border border-green-200 px-2 py-1 text-xs font-medium text-green-700 transition duration-200 hover:bg-green-100 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-900/30"
              >
                <MdTrendingUp className="h-3 w-3" />
                Enable
              </button>
            ) : (
              <>
                {rowData.type === "Mentor" ? (
                  <button
                    onClick={() => handleRoleChange(rowData.uid, userName, 'demote')}
                    disabled={isLoading}
                    className={`flex items-center gap-1 rounded-md bg-gray-50 border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 transition duration-200 hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isLoading ? (
                      <div className="animate-spin h-3 w-3 border-2 border-gray-700 dark:border-gray-300 border-t-transparent rounded-full"></div>
                    ) : (
                      <MdTrendingDown className="h-3 w-3" />
                    )}
                    Demote
                  </button>
                ) : (
                  <button
                    onClick={() => handleRoleChange(rowData.uid, userName, 'promote')}
                    disabled={isLoading}
                    className={`flex items-center gap-1 rounded-md bg-green-50 border border-green-200 px-2 py-1 text-xs font-medium text-green-700 transition duration-200 hover:bg-green-100 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-900/30 ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isLoading ? (
                      <div className="animate-spin h-3 w-3 border-2 border-green-700 dark:border-green-300 border-t-transparent rounded-full"></div>
                    ) : (
                      <MdTrendingUp className="h-3 w-3" />
                    )}
                    Promote
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    }),
  ]; // eslint-disable-next-line
  const [data, setData] = React.useState(() => [...defaultData]);

  // Update data when tableData prop changes (e.g., after promote/demote)
  React.useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 20,
    });

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  // Custom global filter function to search name (array), email, and type
  const globalFilterFn = React.useCallback((row: any, _columnId: string, filterValue: string) => {
    const search = filterValue.toLowerCase();
    const rowData = row.original;

    // Search in name (extract from array)
    const name = Array.isArray(rowData.name) ? rowData.name[0] : rowData.name;
    if (name && name.toLowerCase().includes(search)) {
      return true;
    }

    // Search in email
    if (rowData.email && rowData.email.toLowerCase().includes(search)) {
      return true;
    }

    // Search in type
    if (rowData.type && rowData.type.toLowerCase().includes(search)) {
      return true;
    }

    return false;
  }, []);

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: globalFilterFn,
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  });

  return (
    <Card extra={"w-full h-full sm:overflow-auto p-0"}>
      {/* Header Section */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 px-6 py-4">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {tenantName || 'Organization'} ({table.getFilteredRowModel().rows.length} users)
          </p>
        </div>
        
        {/* Search Bar */}
        <div className="flex h-[42px] w-[320px] items-center rounded-lg border border-gray-200 bg-white px-3 dark:border-gray-600 dark:bg-navy-800">
          <SearchIcon />
          <input
            value={globalFilter ?? ""}
            onChange={(e: any) => setGlobalFilter(e.target.value)}
            type="text"
            placeholder="Search users..."
            className="ml-2 block w-full bg-transparent text-sm text-navy-700 placeholder-gray-500 outline-none dark:text-white dark:placeholder-gray-400"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-navy-800">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer px-6 py-4 text-left font-semibold"
                    >
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <div className="flex items-center">
                            {header.column.getIsSorted() === 'asc' ? (
                              <svg className="w-4 h-4 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <svg className="w-4 h-4 text-brand-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {table
              .getRowModel()
              .rows.map((row, index) => {
                return (
                  <tr key={row.id} className={`hover:bg-brand-50 dark:hover:bg-navy-800 transition-colors ${index % 2 === 0 ? 'bg-white dark:bg-navy-900' : 'bg-brand-50/30 dark:bg-navy-800/50'}`}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          className="px-6 py-4 text-sm"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
        {/* pagination */}
        <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-navy-800 px-6 py-4">
          <div className="flex items-center justify-between">
            {/* left side - showing info */}
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-medium text-navy-700 dark:text-white">{table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}</span> to <span className="font-medium text-navy-700 dark:text-white">{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)}</span> of <span className="font-medium text-navy-700 dark:text-white">{table.getFilteredRowModel().rows.length}</span> results
              </p>
            </div>
            
            {/* right side - pagination controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border transition duration-200 ${
                  !table.getCanPreviousPage() 
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                }`}
              >
                <MdChevronLeft className="text-lg" />
              </button>

              <div className="flex items-center gap-1">
                {createPages(table.getPageCount()).slice(
                  Math.max(0, pageIndex - 2),
                  Math.min(table.getPageCount(), pageIndex + 3)
                ).map((pageNumber, index) => {
                  return (
                    <button
                      className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition duration-200 ${
                        pageNumber === pageIndex + 1
                          ? "bg-brand-500 text-white shadow-sm hover:bg-brand-600"
                          : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                      }`}
                      onClick={() => table.setPageIndex(pageNumber - 1)}
                      key={index}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border transition duration-200 ${
                  !table.getCanNextPage()
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-brand-300 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                }`}
              >
                <MdChevronRight className="text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      {/* User Preferences Modal */}
      <UserPreferencesModal
        isOpen={isPreferencesOpen}
        onClose={() => {
          setIsPreferencesOpen(false);
          setPreferencesUser(null);
        }}
        user={preferencesUser}
        tenantId={tenantId}
        onSaved={async (uid) => {
          userDataService.invalidateUserCache(uid);
          if (tenantId) {
            userDataService.invalidateTenantUsersCache(tenantId);
          }
          if (onUpdateSingleUser) {
            await onUpdateSingleUser(uid);
          }
        }}
      />

      {/* Confirm Popup */}
      <ConfirmPopup
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        variant="default"
      />
    </Card>
  );
}

export default CheckTable;
const columnHelper = createColumnHelper<RowObj>();
